import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';

import Form from '../../Atoms/Form';
import Input from '../../Atoms/Input';
import Select from '../../Atoms/Select';
import Button from '../../Atoms/Button';

import Countries from '../../../shared/Data/countries_br.json';
import CountriesInt from '../../../shared/Data/countries_int.json';
import States from '../../../shared/Data/iso-3166-2.json';

import './index.sass';

// `address` is what the visitor typed during this visit, never data from the API.
const AddressForm = ({ address: receivedAddress, loading, onSave, onCancel, onChange, intl }) => {
	const [address, setState] = useState(receivedAddress || {});

	// Keeps what was typed when the visitor goes back or closes the modal.
	useEffect(() => {
		onChange(address);
	}, [address]); // eslint-disable-line react-hooks/exhaustive-deps
	const [isLoadingAddress, setIsLoadingAddress] = useState(false);
	const [addressError, setAddressError] = useState('');

	const onChangeAddressField = (fieldName, value) => {
		const updatedAddress = { ...address };

		updatedAddress[fieldName] = value;

		// Clear state when country changes
		if (fieldName === 'country') {
			updatedAddress.state = '';
			updatedAddress.city = '';
			updatedAddress.neighborhood = '';
			updatedAddress.street = '';
		}

		setState({ ...updatedAddress });
		setAddressError('');
	};

	// Helper function to map state codes to ISO format
	const mapStateToISO = (stateCode, country) => {
		if (country === 'br') {
			// For Brazil, convert "SP" to "BR-SP" format
			return `BR-${stateCode}`;
		}
		// For other countries, try to find the matching state code
		const countryCode = country.toUpperCase();
		const countryStates = States[countryCode];
		
		if (countryStates && countryStates.divisions) {
			// Find the state code that matches the abbreviation
			const stateEntry = Object.entries(countryStates.divisions).find(([code, name]) => {
				// Check if the code ends with the state abbreviation
				return code.endsWith(stateCode) || code === stateCode;
			});
			
			if (stateEntry) {
				return stateEntry[0];
			}
		}
		
		return stateCode;
	};

	const fetchAddressByZipcode = async (zipcode, country) => {
		setIsLoadingAddress(true);
		setAddressError('');

		try {
			let addressData = null;

			if (country === 'br') {
				// Use ViaCEP for Brazil
				const cleanZipcode = zipcode.replace(/\D/g, '');
				if (cleanZipcode.length !== 8) {
					throw new Error('CEP deve ter 8 dígitos');
				}

				const response = await fetch(`https://viacep.com.br/ws/${cleanZipcode}/json/`);
				const data = await response.json();

				if (data.erro) {
					throw new Error('CEP não encontrado');
				}

				addressData = {
					street: data.logradouro,
					city: data.localidade,
					state: mapStateToISO(data.uf, country),
					neighborhood: data.bairro,
					zipcode: data.cep
				};
			} else {
				// Use Zippopotam.us for international addresses
				const response = await fetch(`https://api.zippopotam.us/${country}/${zipcode}`);
				
				if (!response.ok) {
					throw new Error('Código postal não encontrado');
				}

				const data = await response.json();
				
				if (data.places && data.places.length > 0) {
					const place = data.places[0];
					addressData = {
						city: place['place name'],
						state: mapStateToISO(place['state abbreviation'], country),
						neighborhood: place['place name'],
						zipcode: data['post code']
					};
				} else {
					throw new Error('Endereço não encontrado');
				}
			}

			// Update address with fetched data
			setState(prevAddress => ({
				...prevAddress,
				...addressData
			}));

		} catch (error) {
			setAddressError(error.message);
		} finally {
			setIsLoadingAddress(false);
		}
	};

	const handleZipcodeChange = (e) => {
		const value = e.target.value;
		onChangeAddressField('zipcode', value);

		// Reset all fields except country when zipcode is cleared
		if (!value) {
			setState(prevAddress => ({
				...prevAddress,
				street: '',
				street_number: '',
				city: '',
				state: '',
				neighborhood: ''
			}));
			return;
		}

		// Auto-fetch address when zipcode is complete
		if (address.country && value) {
			const cleanZipcode = value.replace(/\D/g, '');
			
			if (address.country === 'br' && cleanZipcode.length === 8) {
				fetchAddressByZipcode(value, address.country);
			} else if (address.country !== 'br' && cleanZipcode.length >= 3) {
				// For international, try to fetch after a short delay
				setTimeout(() => {
					fetchAddressByZipcode(value, address.country);
				}, 1000);
			}
		}
	};

	const { formatMessage } = intl;
	const messages = defineMessages({
		street: {
			id: 'addressForm.street',
			defaultMessage: 'Rua'
		},
		number: {
			id: 'addressForm.number',
			defaultMessage: 'Número'
		},
		zipcode: {
			id: 'addressForm.zipcode',
			defaultMessage: 'CEP'
		},
		zipcodeInt: {
			id: 'addressForm.zipcodeInt',
			defaultMessage: 'Código Postal'
		},
		city: {
			id: 'addressForm.city',
			defaultMessage: 'Cidade'
		},
		state: {
			id: 'addressForm.state',
			defaultMessage: 'Estado'
		},
		country: {
			id: 'addressForm.country',
			defaultMessage: 'País'
		},
		neighborhood: {
			id: 'addressForm.neighborhood',
			defaultMessage: 'Bairro'
		},
		next: {
			id: 'addressForm.next',
			defaultMessage: 'Próximo'
		},
		changeMyInfo: {
			id: 'addressForm.changeMyInfo',
			defaultMessage: 'Alterar meus dados'
		},
		loadingAddress: {
			id: 'addressForm.loadingAddress',
			defaultMessage: 'Carregando endereço...'
		}
	});
	const locale = localStorage.getItem('locale') || 'pt';

	// Filter states based on selected country
	const availableStates = useMemo(() => {
		if (!address.country) return [];
		
		const countryCode = address.country.toUpperCase();
		const countryStates = States[countryCode];
		
		if (!countryStates || !countryStates.divisions) return [];
		
		// Convert divisions to the format expected by Select component
		return Object.entries(countryStates.divisions).map(([code, name]) => ({
			label: name,
			value: code
		}));
	}, [address.country]);

	// Get the appropriate zipcode label based on country
	const getZipcodeLabel = () => {
		return address.country === 'br' 
			? formatMessage(messages.zipcode) 
			: formatMessage(messages.zipcodeInt);
	};

	return (
		<>
			<Form
				id="address_form"
				className="AddressForm"
				onSubmit={(e) => {
					e.preventDefault()
					onSave(address)
				}}
			>
				<Select
					id="country-select"
					label={formatMessage(messages.country)}
					className="AddressForm__input AddressForm__input--full"
					value={address.country || ''}
					options={locale !== 'pt' ? CountriesInt : Countries}
					required
					onChange={(e) => onChangeAddressField('country', e.target.value)}
				/>
				
				{address.country && (
					<Input
						id="zipcode"
						className="AddressForm__input"
						type="text"
						label={getZipcodeLabel()}
						value={address.zipcode || ''}
						required
						onChange={handleZipcodeChange}
						placeholder={address.country === 'br' ? '00000-000' : 'Digite o código postal'}
						disabled={isLoadingAddress}
						name="postal-code"
						autoComplete="postal-code"
					/>
				)}

				{isLoadingAddress && (
					<div className="AddressForm__loading">
						{formatMessage(messages.loadingAddress)}
					</div>
				)}

				{addressError && (
					<div className="AddressForm__error">
						{addressError}
					</div>
				)}

				<div style={{ display: 'grid', gridAutoFlow: 'column', gridTemplateColumns: '3fr 1fr', gap: '1rem' }}>
					<Input
						id="street"
						className="AddressForm__input"
						type="text"
						label={formatMessage(messages.street)}
						name="street"
						value={address.street || ''}
						required
						onChange={(e) => onChangeAddressField('street', e.target.value)}
						autoComplete="address-line1"
					/>
					<Input
						id="street_number"
						className="AddressForm__input"
						type="text"
						label={formatMessage(messages.number)}
						value={address.street_number || ''}
						required
						onChange={(e) => onChangeAddressField('street_number', e.target.value)}
						name="street_number"
					/>
				</div>

				<Select
					id="state"
					label={formatMessage(messages.state)}
					className="AddressForm__input"
					value={address.state || ''}
					options={availableStates}
					required
					disabled={!address.country || availableStates.length === 0}
					onChange={(e) => onChangeAddressField('state', e.target.value)}
				/>
				<Input
					id="city"
					className="AddressForm__input"
					type="text"
					label={formatMessage(messages.city)}
					value={address.city || ''}
					required
					onChange={(e) => onChangeAddressField('city', e.target.value)}
					name="city"
					autoComplete="address-level2"
				/>
				<Input
					id="neighborhood"
					className="AddressForm__input"
					type="text"
					label={formatMessage(messages.neighborhood)}
					value={address.neighborhood || ''}
					required
					onChange={(e) => onChangeAddressField('neighborhood', e.target.value)}
					name="neighborhood"
					autoComplete="address-level3"
				/>
				<div id="field_errors" />
				<Button
					className="AddressForm__save"
					color="secondary"
					size="medium"
					type="submit"
					disabled={!address?.country}
				>
					{formatMessage(messages.next)}
				</Button>
			</Form>
			{!loading && (
				<Button
					className="AddressForm__close"
					color="primary"
					isLink
					onClick={onCancel}
					size="medium"
				>
					{formatMessage(messages.changeMyInfo)}
				</Button>
			)}
		</>
	)
}

AddressForm.propTypes = {
	address: PropTypes.shape({}),
	onSave: PropTypes.func,
	onChange: PropTypes.func,
	intl: PropTypes.shape().isRequired
}

AddressForm.defaultProps = {
	address: {},
	onSave: () => {},
	onChange: () => {}
}

export default injectIntl(AddressForm);
